/** Admin Projects listing fixtures + API mappers. */

import dayjs from "dayjs"

export const PROJECT_KPI = [
  {
    key: "total",
    title: "Total Projects",
    value: "--",
    tone: "lightblue-custom",
  },
  {
    key: "present",
    title: "Active",
    value: "--",
    tone: "lightteal",
  },
  {
    key: "past",
    title: "Past",
    value: "--",
    tone: "red",
  },
  {
    key: "future",
    title: "Future (Scheduled)",
    value: "--",
    tone: "purple-custom",
  },
]

export const PROJECT_STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "present", label: "Present" },
  { key: "past", label: "Past" },
  { key: "future", label: "Future" },
]

export const PROJECT_SORT_OPTIONS = [
  { value: "name", label: "Name" },
  { value: "start_date", label: "Start Date" },
  { value: "end_date", label: "End Date" },
  { value: "created_at", label: "Created At" },
  { value: "updated_at", label: "Updated At" },
]

export const DEFAULT_PROJECT_SORT_BY = "created_at"
export const DEFAULT_PROJECT_SORT_DIRECTION = "desc"

/** GET /api/v1/admin/projects list eager-load. */
export const PROJECT_LIST_WITH = ["categories", "cities.city"]

/** Single-city filter — projects API uses project_city_ids[]=UUID */
export const appendProjectCityFilter = (params, cityFilter = "") => {
  const cityId = Array.isArray(cityFilter)
    ? String(cityFilter[0] || "")
    : String(cityFilter || "")

  if (cityId) params["project_city_ids[]"] = cityId
}

export const PROJECT_ROWS = []

const formatCount = value => {
  const num = Number(value)
  if (!Number.isFinite(num)) return "--"
  return num.toLocaleString("en-US")
}

const getInitials = name =>
  (name || "")
    .split(/\s+/)
    .map(part => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase()

const deriveStatus = (startDate, endDate) => {
  const now = dayjs()
  const start = startDate ? dayjs(startDate) : null
  if (!start || !start.isValid()) return "future"
  if (now.isBefore(start, "day")) return "future"
  const end = endDate ? dayjs(endDate) : null
  if (end && end.isValid() && now.isAfter(end, "day")) return "past"
  return "present"
}

const formatProjectCityEntry = city => {
  if (!city) return ""
  if (typeof city === "string") return city.trim()
  const name = city.city || city.name || ""
  const state = city.state || city.country || ""
  if (name && state) return `${name}, ${state}`
  return name || state || ""
}

/** Resolve city label from with[]=cities.city payload. */
export const resolveProjectCityLabel = item => {
  const cities = Array.isArray(item?.cities) ? item.cities : []
  const labels = cities
    .map(entry => formatProjectCityEntry(entry?.city))
    .filter(Boolean)

  if (labels.length) return labels.join(", ")

  return (
    formatProjectCityEntry(item?.city) ||
    item?.city_name ||
    (typeof item?.city === "string" ? item.city : "") ||
    "--"
  )
}

/** Map GET /api/v1/admin/projects item → table row. */
export const mapProjectRow = item => {
  const categories = Array.isArray(item.categories)
    ? item.categories
        .map(cat => (typeof cat === "string" ? cat : cat?.name))
        .filter(Boolean)
    : []
  const members = Array.isArray(item.members) ? item.members : []
  const membersCount = Number(
    item.members_count ??
      item.membersCount ??
      item.total_members ??
      item.collaborators_count ??
      members.length,
  )
  const status =
    item.status === "present" ||
    item.status === "past" ||
    item.status === "future"
      ? item.status
      : deriveStatus(item.start_date, item.end_date)

  return {
    id: item.id,
    title: item.name || "--",
    initials: getInitials(item.name),
    category: categories.length ? categories.join(", ") : "--",
    city: resolveProjectCityLabel(item),
    members: Number.isFinite(membersCount) ? membersCount : 0,
    status,
    startDate: item.start_date
      ? dayjs(item.start_date).format("MMMM D, YYYY")
      : "--",
  }
}

/** Map GET /api/v1/projects/summary → KPI card values. */
export const mapProjectKpi = (result = {}) => {
  const present = Number(result.present_count ?? result.present ?? result.active)
  const past = Number(result.past_count ?? result.past)
  const future = Number(result.future_count ?? result.future)
  const total = Number(
    result.total_projects ??
      result.total ??
      (Number.isFinite(present) && Number.isFinite(past) && Number.isFinite(future)
        ? present + past + future
        : NaN),
  )

  return {
    total: formatCount(total),
    present: formatCount(present),
    past: formatCount(past),
    future: formatCount(future),
  }
}

/** Map GET /api/v1/projects/summary → ListingTabs with counts. */
export const mapProjectStatusTabs = (
  result = {},
  baseTabs = PROJECT_STATUS_TABS,
) => {
  const present = Number(result.present_count ?? result.present ?? result.active)
  const past = Number(result.past_count ?? result.past)
  const future = Number(result.future_count ?? result.future)
  const total = Number(
    result.total_projects ??
      result.total ??
      (Number.isFinite(present) && Number.isFinite(past) && Number.isFinite(future)
        ? present + past + future
        : NaN),
  )

  const countByKey = {
    all: Number.isFinite(total) ? total : undefined,
    present: Number.isFinite(present) ? present : undefined,
    past: Number.isFinite(past) ? past : undefined,
    future: Number.isFinite(future) ? future : undefined,
  }

  return baseTabs.map(tab => {
    const count = countByKey[tab.key]
    if (typeof count !== "number") return { ...tab }
    return { ...tab, count }
  })
}
